import type { FC, ReactNode, SVGProps } from 'react'
import type { LayoutData } from '@/types/content'

import { cn } from '@/lib/utils'

import {
    AppleMusicIcon,
    FacebookIcon,
    InstagramIcon,
    JioSaavnIcon,
    SoundCloudIcon,
    SpotifyIcon,
    XIcon,
    YouTubeIcon
} from '@/components/media/brand-icons'

type Socials = Pick<LayoutData['artist'], 'youtube_url' | 'spotify_url' | 'instagram_url' | 'apple_music_url' | 'jiosaavn_url' | 'soundcloud_url' | 'facebook_url' | 'x_url'>

const platforms: Array<{ key: keyof Socials, label: string, Icon: FC<SVGProps<SVGSVGElement>> }> = [
    { key: 'youtube_url', label: 'YouTube', Icon: YouTubeIcon },
    { key: 'spotify_url', label: 'Spotify', Icon: SpotifyIcon },
    { key: 'instagram_url', label: 'Instagram', Icon: InstagramIcon },
    { key: 'apple_music_url', label: 'Apple Music', Icon: AppleMusicIcon },
    { key: 'jiosaavn_url', label: 'JioSaavn', Icon: JioSaavnIcon },
    { key: 'soundcloud_url', label: 'SoundCloud', Icon: SoundCloudIcon },
    { key: 'facebook_url', label: 'Facebook', Icon: FacebookIcon },
    { key: 'x_url', label: 'X', Icon: XIcon }
]

export const socialEntries = (socials: Socials): Array<{ label: string, href: string, Icon: FC<SVGProps<SVGSVGElement>> }> =>
    platforms
        .filter(platform => Boolean(socials[platform.key]))
        .map(platform => ({ label: platform.label, href: socials[platform.key] as string, Icon: platform.Icon }))

interface SocialLinksProps {
    socials: Socials
    className?: string
    showLabels?: boolean
}

export const SocialLinks: FC<SocialLinksProps> = ({ socials, className, showLabels = false }): ReactNode => {
    const entries = socialEntries(socials)

    if (entries.length === 0) return null

    return (
        <ul className={cn('flex flex-wrap items-center gap-2', className)}>
            {entries.map(({ label, href, Icon }) => (
                <li key={label}>
                    <a
                        href={href}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={cn(
                            'inline-flex items-center gap-2 rounded-full border border-ivory/10 text-ivory/70 transition duration-300 hover:border-gold/50 hover:text-ivory',
                            showLabels ? 'px-3.5 py-2 text-xs' : 'size-10 justify-center'
                        )}
                        aria-label={showLabels ? undefined : `Serene Corpse on ${label}`}
                    >
                        <Icon className='size-[18px]' />
                        {showLabels && <span>{label}</span>}
                    </a>
                </li>
            ))}
        </ul>
    )
}
